import { useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { getTenantCode } from '../../auth/authApi.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { LoginShell } from '../../../../shared/auth/LoginShell.js';

type LoginMethod = 'PASSWORD' | 'MOBILE_OTP' | 'EMAIL_OTP';

const CHANGE_PASSWORD_TOKEN_KEY = 'tradelogic.auth.change-password-token';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^\+?[0-9]{7,15}$/;
const OTP_RE = /^[0-9]{4,8}$/;

function persistChangePasswordToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(CHANGE_PASSWORD_TOKEN_KEY, token);
  } catch {
    // sessionStorage can be unavailable in privacy modes; the redirect still
    // carries the token in state and query string for the active tab.
  }
}

/**
 * Read a password-change instruction out of a failed login.
 *
 * The destination is the server's to choose, so it is taken from the response
 * rather than written here; only the grant's presence is required, because a
 * change screen with nothing to spend cannot do anything.
 */
function passwordChangeRedirect(err: unknown): { path: string; changeToken: string } | null {
  const body = (err as { body?: unknown } | null)?.body;
  if (typeof body !== 'object' || body === null) return null;
  const { code, redirect, changeToken } = body as Record<string, unknown>;
  if (code !== 'EX-PWD-03') return null;
  if (typeof redirect !== 'string' || typeof changeToken !== 'string') return null;
  return { path: redirect, changeToken };
}

export function LoginPage(): ReactNode {
  const { login, requestOtp, verifyOtp } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [method, setMethod] = useState<LoginMethod>('PASSWORD');
  // Prefilled from the last sign-in (or the dev build default) so returning
  // users need not retype it; still editable to switch tenant.
  const [tenantCode, setTenantCode] = useState(getTenantCode());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // OTP flow state.
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpToken, setOtpToken] = useState<string | null>(null);
  const [otpNotice, setOtpNotice] = useState<string | null>(null);

  function switchMethod(next: LoginMethod): void {
    setMethod(next);
    setError(null);
    setOtpToken(null);
    setOtpCode('');
    setOtpNotice(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!tenantCode.trim()) { setError(t('auth.login.error.tenantRequired')); return; }
    if (!username.trim()) { setError(t('auth.login.error.usernameRequired')); return; }
    // [DEV-BYPASS] In dev mode we allow the canonical test password 'a' so
    // the team can walk every screen without a live backend. Production
    // builds (`import.meta.env.DEV === false`) still enforce 8+ chars.
    // The bypass is narrow: ONLY exact match `'a'` skips the length check.
    // Any other short password still fails validation — including in dev —
    // so the UX matches the production rule. See StartBMS.bat.
    const devEnv = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
    const devSentinel = devEnv && password === 'a';
    if (!devSentinel && password.length < 8) {
      setError(t('auth.login.error.passwordLength'));
      return;
    }
    setBusy(true);
    try {
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.removeItem(CHANGE_PASSWORD_TOKEN_KEY);
        } catch {
          // The grant is single-use and short-lived; storage is best-effort only.
        }
      }
      const { requiresMfa } = await login(username.trim(), password, tenantCode.trim());
      navigate(requiresMfa ? '/auth/mfa' : '/dashboard');
    } catch (err) {
      // An expired password is not a dead end, it is an instruction. The
      // server answers EX-PWD-03 with the route to go to and the single-use
      // grant that authorises it; showing only the message left the user
      // staring at a red box with nowhere to go.
      const redirect = passwordChangeRedirect(err);
      if (redirect) {
        persistChangePasswordToken(redirect.changeToken);
        const url = new URL(redirect.path, window.location.origin);
        url.searchParams.set('token', redirect.changeToken);
        navigate(`${url.pathname}${url.search}`, {
          state: { changeToken: redirect.changeToken },
          replace: true,
        });
        return;
      }
      setError((err as Error).message || t('auth.login.error.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function onSendOtp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!tenantCode.trim()) { setError(t('auth.login.error.tenantRequired')); return; }
    const id = otpIdentifier.trim();
    if (!id) { setError(method === 'EMAIL_OTP' ? t('auth.login.otp.emailRequired') : t('auth.login.otp.mobileRequired')); return; }
    if (method === 'EMAIL_OTP' && !EMAIL_RE.test(id)) { setError(t('auth.login.otp.invalidEmail')); return; }
    if (method === 'MOBILE_OTP' && !MOBILE_RE.test(id)) { setError(t('auth.login.otp.invalidMobile')); return; }
    setBusy(true);
    try {
      const res = await requestOtp(id, method as 'MOBILE_OTP' | 'EMAIL_OTP', tenantCode.trim());
      setOtpToken(res.otpToken);
      setOtpNotice(res.delivery ? t('auth.login.otp.sentVia', { delivery: res.delivery }) : t('auth.login.otp.sent'));
    } catch (err) {
      setError((err as Error).message || t('auth.login.otp.couldNotSend'));
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!OTP_RE.test(otpCode.trim())) { setError(t('auth.login.otp.invalidCode')); return; }
    if (!otpToken) { setError(t('auth.login.otp.requestFirst')); return; }
    setBusy(true);
    try {
      await verifyOtp(otpToken, otpCode.trim());
      navigate('/dashboard');
    } catch (err) {
      setError((err as Error).message || t('auth.login.otp.verifyFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="login">
      <LoginShell
        title={t('auth.login.title')}
        subtitle={t('auth.login.subtitle')}
      >
        <div className="login-field">
          <label htmlFor="login-method">{t('auth.login.method.label')}</label>
          <div className="login-control">
            <select
              id="login-method"
              value={method}
              onChange={(e) => switchMethod(e.target.value as LoginMethod)}
            >
              <option value="PASSWORD">{t('auth.login.method.password')}</option>
              <option value="MOBILE_OTP">{t('auth.login.method.mobile')}</option>
              <option value="EMAIL_OTP">{t('auth.login.method.email')}</option>
            </select>
          </div>
        </div>

        {method === 'PASSWORD' ? (
          <form onSubmit={onSubmit} aria-label="login-form">
            <div className="login-field login-field--row">
              <div>
                <label htmlFor="tenant-code">{t('auth.login.tenantCode')}</label>
                <div className="login-control">
                  <input id="tenant-code" name="tenantCode" value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} autoComplete="organization" />
                </div>
              </div>
              <div>
                <label htmlFor="username">{t('auth.login.employeeCode')}</label>
                <div className="login-control">
                  <input id="username" name="username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
                </div>
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password">{t('auth.login.password')}</label>
              <div className="login-control login-control--with-toggle">
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                <button type="button" className="login-toggle" aria-pressed={showPassword} aria-controls="password" onClick={() => setShowPassword((v) => !v)}>
                  {showPassword ? t('auth.login.hide') : t('auth.login.show')}
                </button>
              </div>
            </div>

            {error ? <p role="alert" className="login-alert">{error}</p> : null}

            <p className="login-options"><a href="/auth/forgot">{t('auth.login.forgot')}</a></p>

            <button type="submit" className="login-submit" disabled={busy}>
              <span>{busy ? t('auth.login.signingIn') : t('auth.login.signin')}</span>
              <span aria-hidden="true">→</span>
            </button>
          </form>
        ) : (
          <form onSubmit={otpToken ? onVerifyOtp : onSendOtp} aria-label="otp-form" noValidate>
            <div className="login-field">
              <label htmlFor="otp-tenant-code">{t('auth.login.tenantCode')}</label>
              <div className="login-control">
                <input
                  id="otp-tenant-code"
                  name="otpTenantCode"
                  value={tenantCode}
                  onChange={(e) => setTenantCode(e.target.value)}
                  autoComplete="organization"
                  readOnly={Boolean(otpToken)}
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="otp-identifier">{method === 'EMAIL_OTP' ? t('auth.login.otp.emailAddress') : t('auth.login.otp.mobileNumber')}</label>
              <div className="login-control">
                <input
                  id="otp-identifier"
                  name="otp-identifier"
                  value={otpIdentifier}
                  onChange={(e) => setOtpIdentifier(e.target.value)}
                  autoComplete={method === 'EMAIL_OTP' ? 'email' : 'tel'}
                  readOnly={Boolean(otpToken)}
                  inputMode={method === 'EMAIL_OTP' ? 'email' : 'tel'}
                />
              </div>
            </div>

            {otpToken ? (
              <div className="login-field">
                <label htmlFor="otp-code">{t('auth.login.otp.code')}</label>
                <div className="login-control">
                  <input
                    id="otp-code"
                    name="otp-code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                  />
                </div>
              </div>
            ) : null}

            {otpNotice ? <p role="status" className="login-note">{otpNotice}</p> : null}
            {error ? <p role="alert" className="login-alert">{error}</p> : null}

            <p className="login-options"><a href="/auth/forgot">{t('auth.login.forgot')}</a></p>

            <button type="submit" className="login-submit" disabled={busy}>
              <span>
                {otpToken
                  ? (busy ? t('auth.login.verifying') : t('auth.login.verifyAndSignin'))
                  : (busy ? t('auth.login.sending') : t('auth.login.sendCode'))}
              </span>
              <span aria-hidden="true">→</span>
            </button>
          </form>
        )}
      </LoginShell>
    </main>
  );
}
